import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import { Capacitor } from "@capacitor/core";
import { WidgetSync, PendingIntentAction } from "@/plugins/widget-sync";
import { useTrackingActions } from "@/hooks/use-tracking-actions";
import { toast } from "@/hooks/use-toast";

/**
 * Hook to check for and handle pending intent actions from Siri/widgets.
 * Must be called inside a component that has access to DashboardContext.
 */
export function useIntentActions() {
  const navigate = useNavigate();
  const isNative = Capacitor.isNativePlatform();
  const hasChecked = useRef(false);
  const isProcessing = useRef(false);

  const {
    startCommute,
    startBlock,
    stopCurrentTracking,
    pauseCurrentTracking,
    resumeCurrentTracking,
    isReady,
    hasActiveBlock,
    isShiftActive,
    isPaused,
  } = useTrackingActions();

  const handlePendingAction = useCallback(
    async (action: PendingIntentAction) => {
      if (!action.hasPendingAction) return;
      if (isProcessing.current) return;

      isProcessing.current = true;
      console.log("[IntentActions] Processing:", action);

      try {
        switch (action.action) {
          case "deepLink":
            if (action.deepLink === "add-block") {
              navigate("/blocks/new");
              toast({
                title: "Add Block",
                description: "Ready to log a new block",
              });
            } else if (action.deepLink === "settings") {
              navigate("/settings");
            }
            break;

          case "start":
            if (action.type === "commute") {
              // Wait for location to be ready
              if (!isReady) {
                toast({
                  title: "Starting commute...",
                  description: "Waiting for location services",
                });
                // Retry after a short delay
                setTimeout(async () => {
                  await startCommute();
                  isProcessing.current = false;
                }, 1500);
                return;
              }
              await startCommute();
            } else {
              // type === "block"
              if (!hasActiveBlock) {
                toast({
                  title: "No active block",
                  description: "Add a block first, then try again",
                  variant: "destructive",
                });
                navigate("/blocks/new");
              } else if (!isReady) {
                toast({
                  title: "Starting route...",
                  description: "Waiting for location services",
                });
                setTimeout(async () => {
                  await startBlock();
                  isProcessing.current = false;
                }, 1500);
                return;
              } else {
                await startBlock();
              }
            }
            break;

          case "stop":
            await stopCurrentTracking();
            break;

          case "pause":
            console.log("[IntentActions] Calling pauseCurrentTracking, isShiftActive:", isShiftActive);
            // If shift data isn't loaded yet, retry after a delay
            if (!isShiftActive) {
              console.log("[IntentActions] Shift not loaded yet, retrying pause in 1.5s...");
              setTimeout(async () => {
                await pauseCurrentTracking();
                isProcessing.current = false;
              }, 1500);
              return;
            }
            await pauseCurrentTracking();
            break;

          case "resume":
            console.log("[IntentActions] Calling resumeCurrentTracking, isPaused:", isPaused);
            // If shift data isn't loaded yet, retry after a delay
            if (!isPaused) {
              console.log("[IntentActions] Shift not loaded yet, retrying resume in 1.5s...");
              setTimeout(async () => {
                await resumeCurrentTracking();
                isProcessing.current = false;
              }, 1500);
              return;
            }
            await resumeCurrentTracking();
            break;
        }
      } finally {
        isProcessing.current = false;
      }
    },
    [navigate, isReady, hasActiveBlock, isShiftActive, isPaused, startCommute, startBlock, stopCurrentTracking, pauseCurrentTracking, resumeCurrentTracking]
  );

  // Check for pending actions on mount and when app becomes active
  useEffect(() => {
    if (!isNative) return;

    const checkPendingActions = async () => {
      console.log("[IntentActions] Checking for pending actions on mount...");
      try {
        const action = await WidgetSync.getPendingIntentAction();
        console.log("[IntentActions] Mount check result:", JSON.stringify(action));
        if (action.hasPendingAction) {
          // Small delay to ensure app is fully loaded
          setTimeout(() => handlePendingAction(action), 500);
        }
      } catch (error) {
        console.error("[IntentActions] Error checking pending actions:", error);
      }
    };

    // Check immediately on mount (only once)
    if (!hasChecked.current) {
      hasChecked.current = true;
      checkPendingActions();
    }

    // Also check when app resumes from background
    // Use polling to ensure we catch the action after AppDelegate processes it
    const handleResume = () => {
      console.log("[IntentActions] === APP RESUMED === Starting polling for pending actions");

      // Poll multiple times with increasing delays
      const delays = [100, 300, 600, 1000, 1500];
      delays.forEach((delay) => {
        setTimeout(async () => {
          try {
            const action = await WidgetSync.getPendingIntentAction();
            console.log(`[IntentActions] Check at ${delay}ms:`, action);
            if (action.hasPendingAction) {
              handlePendingAction(action);
            }
          } catch (error) {
            console.error(`[IntentActions] Error at ${delay}ms:`, error);
          }
        }, delay);
      });
    };

    document.addEventListener("resume", handleResume);

    // iOS visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleResume();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("resume", handleResume);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isNative, handlePendingAction]);
}
